<template>
    <div class="listener-groupcast">
        <section class="listener-groupcast__hero">
            <div class="listener-groupcast__topbar">
                <button class="listener-groupcast__icon-button" @click="goBack">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M15 18 9 12l6-6"></path>
                    </svg>
                </button>
                <div class="listener-groupcast__heading">
                    <div class="listener-groupcast__eyebrow">{{ $tt('同步播放') }}</div>
                    <div class="listener-groupcast__caption">{{ syncRoomId ? roomMetaText : $tt('让多台设备一起进入同一份节奏') }}</div>
                </div>
                <button class="listener-groupcast__icon-button" @click="goNowPlaying">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M5 5.5A2.5 2.5 0 0 1 7.5 3h9A2.5 2.5 0 0 1 19 5.5v13a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 5 18.5zm5 3.2v6.6a.8.8 0 0 0 1.22.68l5-3.3a.8.8 0 0 0 0-1.36l-5-3.3A.8.8 0 0 0 10 8.7z"></path>
                    </svg>
                </button>
            </div>

            <div class="listener-groupcast__summary">
                <div class="listener-groupcast__summary-kicker">{{ $tt('多端同播') }}</div>
                <div class="listener-groupcast__summary-title">
                    {{ syncRoomId ? $tt('房间 {room} 正在同步播放', { room: syncRoomId }) : $tt('把这一刻的音乐分享给更多设备') }}
                </div>
                <div class="listener-groupcast__summary-sub">
                    {{ syncRoomId
                        ? $tt('在线 {count} 人，房主控制播放、进度和切歌，成员可以实时收到同步。', { count: syncOnlineCount })
                        : $tt('你可以创建房间、邀请别的设备加入，也可以输入房间号直接进入正在进行的会话。') }}
                </div>
                <div class="listener-groupcast__summary-actions">
                    <button class="listener-groupcast__summary-action listener-groupcast__summary-action--primary" @click="createSyncRoom" :disabled="syncBusy">
                        {{ $tt('创建房间') }}
                    </button>
                    <button class="listener-groupcast__summary-action" @click="leaveSyncRoom" :disabled="syncBusy || !syncRoomId">
                        {{ $tt('离开房间') }}
                    </button>
                </div>
            </div>

            <div class="listener-groupcast__join-card">
                <div class="listener-groupcast__join-kicker">{{ $tt('加入现有房间') }}</div>
                <div class="listener-groupcast__join-row">
                    <input
                        v-model.trim="syncRoomInput"
                        class="listener-groupcast__join-input"
                        :placeholder="$tt('输入房间号')"
                    >
                    <button class="listener-groupcast__join-button" @click="joinSyncRoom" :disabled="syncBusy || !syncRoomInput">
                        {{ $tt('加入') }}
                    </button>
                </div>
                <div v-if="syncReadonly" class="listener-groupcast__join-tip">
                    {{ $tt('当前房间由房主控制播放，你可以一起听并参与聊天。') }}
                </div>
                <div
                    v-if="showOwnerReconnectTip"
                    class="listener-groupcast__owner-tip"
                >
                    {{ $tt('房主连接中，等待重连...若持续掉线会自动解散会话') }}
                </div>
            </div>
        </section>

        <section class="listener-groupcast__section">
            <div class="listener-groupcast__section-header">
                <h2>{{ $tt('房间成员') }}</h2>
                <button v-if="onlineUsers.length > 0" @click="toggleMembersCollapse">
                    {{ membersCollapsed ? $tt('展开') : $tt('收起') }}
                </button>
            </div>
            <div v-if="membersCollapsed" class="listener-groupcast__members-compact">
                <div>{{ $tt('在线 {count} 人', { count: syncOnlineCount }) }}</div>
                <div v-if="ownerUser" class="truncate">{{ $tt('房主') }}: {{ ownerUser.username }}</div>
                <div v-else class="truncate">{{ $tt('房主暂时离线') }}</div>
            </div>
            <div v-else class="listener-groupcast__members">
                <div
                    v-for="u in onlineUsers"
                    :key="u.user_id"
                    class="listener-groupcast__member"
                >
                    <div class="listener-groupcast__member-avatar" :style="{ background: avatarGradient(u.username) }">
                        {{ getUserInitial(u.username) }}
                    </div>
                    <div class="listener-groupcast__member-meta">
                        <div class="listener-groupcast__member-name">{{ u.username }}</div>
                        <div class="listener-groupcast__member-role">
                            {{ isSameId(u.user_id, syncOwnerUserId)
                                ? (syncOwnerOnline ? $tt('房主') : $tt('房主(重连中)'))
                                : $tt('成员') }}
                        </div>
                    </div>
                    <span v-if="isSameId(u.user_id, syncCurrentUserId)" class="listener-groupcast__member-tag">{{ $tt('我') }}</span>
                </div>
                <div v-if="onlineUsers.length === 0" class="listener-groupcast__empty-inline">{{ $tt('暂无成员') }}</div>
            </div>
        </section>

        <section class="listener-groupcast__section">
            <div class="listener-groupcast__section-header">
                <h2>{{ $tt('同步列表') }}</h2>
                <button @click="goNowPlaying">{{ $tt('当前播放') }}</button>
            </div>
            <div class="listener-groupcast__queue-wrap">
                <ListenerTrackList :tracks="tracks" no-artist index-field="index" show-image :readonly="syncReadonly" />
            </div>
        </section>

        <section class="listener-groupcast__section">
            <div class="listener-groupcast__section-header">
                <h2>{{ $tt('滚动歌词') }}</h2>
                <button v-if="lyricIndex >= 0" @click="scrollActiveLyricIntoView(lyricIndex)">{{ $tt('定位当前行') }}</button>
            </div>
            <div ref="lyricContainer" class="listener-groupcast__lyrics">
                <button
                    v-for="(item, i) in lyricsObjArr"
                    :key="item.uid"
                    :data-index="i"
                    ref="lyric"
                    @click="scrollToLyric(i)"
                    :class="['listener-groupcast__lyric-line', lyricIndex === i ? 'is-active' : '']"
                >
                    {{ item.lyric }}
                </button>
                <div v-if="!lyricsObjArr.length" class="listener-groupcast__empty-inline">{{ $tt('暂无歌词') }}</div>
            </div>
        </section>

        <section class="listener-groupcast__section listener-groupcast__section--chat">
            <div class="listener-groupcast__section-header">
                <h2>{{ $tt('房间聊天') }}</h2>
            </div>
            <div ref="chatScroll" class="listener-groupcast__chat-list">
                <div
                    v-for="msg in chatMessages"
                    :key="msg.id"
                >
                    <div
                        v-if="msg.type === 'system'"
                        class="listener-groupcast__chat-system"
                    >
                        <span>{{ msg.message }}</span>
                    </div>
                    <div
                        v-else
                        :class="['listener-groupcast__chat-bubble-wrap', isMine(msg) ? 'is-mine' : '']"
                    >
                        <div class="listener-groupcast__chat-meta">
                            <span>{{ msg.username }}</span>
                            <span>{{ formatChatTime(msg.created_at_ms) }}</span>
                        </div>
                        <div class="listener-groupcast__chat-bubble">
                            {{ msg.message }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="listener-groupcast__chat-input-row">
                <input
                    v-model.trim="chatInput"
                    class="listener-groupcast__chat-input"
                    :placeholder="$tt('输入消息')"
                    @keyup.enter="sendChat"
                    :disabled="!syncRoomId"
                >
                <button class="listener-groupcast__chat-send" @click="sendChat" :disabled="!syncRoomId || !chatInput">
                    {{ $tt('发送') }}
                </button>
            </div>
        </section>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ListenerTrackList from '@/views/library/track/ListenerTrackList.vue'

const AVATAR_COLORS = [
    ['#38bdf8', '#0284c7'],
    ['#34d399', '#059669'],
    ['#22d3ee', '#0e7490'],
    ['#a78bfa', '#7c3aed'],
    ['#fb7185', '#e11d48'],
    ['#4ade80', '#15803d'],
]

const MEMBER_COLLAPSE_THRESHOLD = 6

export default {
    name: 'ListenerGroupCast',
    components: {
        ListenerTrackList
    },
    data() {
        return {
            syncRoomInput: '',
            syncBusy: false,
            lyricIndex: 0,
            lyricsObjArr: [],
            chatInput: '',
            chatMessages: [],
            membersSnapshotReady: false,
            membersCollapsed: false,
            membersManualToggled: false,
        }
    },
    computed: {
        ...mapGetters(['trackId']),
        tracks() {
            return this.$store.state.common.queue || []
        },
        currentTrack() {
            return this.$store.getters.track
        },
        currentTime() {
            return this.$store.state.common.currentTime
        },
        duration() {
            return this.$store.state.common.duration
        },
        syncRoomId() {
            return this.$store.state.syncRoom.roomId
        },
        syncOnlineCount() {
            return this.$store.state.syncRoom.onlineCount
        },
        syncIsOwner() {
            return this.$store.getters['syncRoom/isOwner']
        },
        syncReadonly() {
            return !!this.syncRoomId && !this.syncIsOwner
        },
        syncOwnerUserId() {
            return this.$store.state.syncRoom.ownerUserId
        },
        syncOwnerOnline() {
            return this.$store.state.syncRoom.ownerOnline !== false
        },
        syncCurrentUserId() {
            return this.$store.state.syncRoom.currentUserId
        },
        onlineUsers() {
            return this.$store.state.syncRoom.onlineUsers || []
        },
        ownerUser() {
            return this.onlineUsers.find((u) => this.isSameId(u.user_id, this.syncOwnerUserId)) || null
        },
        showOwnerReconnectTip() {
            return !!this.syncRoomId && !this.syncIsOwner && !this.syncOwnerOnline
        },
        roomMetaText() {
            return this.$tt('房间 {room} · 在线 {count} 人', { room: this.syncRoomId, count: this.syncOnlineCount })
        }
    },
    created() {
        this.loadCurrentLyrics()
    },
    watch: {
        trackId() {
            this.loadCurrentLyrics()
        },
        currentTime(newTime) {
            if (!this.lyricsObjArr.length) return
            let activeIndex = -1
            for (let i = 0; i < this.lyricsObjArr.length; i += 1) {
                const t = parseFloat(this.lyricsObjArr[i].time)
                if (Number.isNaN(t)) continue
                if (newTime >= t) {
                    activeIndex = i
                } else {
                    break
                }
            }
            if (activeIndex >= 0 && activeIndex !== this.lyricIndex) {
                this.lyricIndex = activeIndex
            }
        },
        lyricIndex(newIndex) {
            this.scrollActiveLyricIntoView(newIndex)
        },
        '$store.state.syncRoom.lastChatMessage': {
            handler(msg) {
                if (!msg) return
                if (msg.type === 'system') {
                    const fallbackId = msg.event === 'playback_action'
                        ? `chat_pb_${msg.created_at_ms}_${msg.action_type || 'event'}`
                        : `${msg.created_at_ms}_${Math.random()}`
                    this.chatMessages.push({
                        ...msg,
                        id: msg.version ? `chat_pb_${msg.version}` : fallbackId,
                        type: 'system',
                    })
                } else {
                    this.chatMessages.push({
                        ...msg,
                        id: `${msg.created_at_ms}_${Math.random()}`,
                        type: 'user',
                    })
                }
                this.scrollChatToBottom()
            },
        },
        syncRoomId(newVal) {
            if (!newVal) {
                this.chatMessages = []
                this.membersSnapshotReady = false
                this.membersCollapsed = false
                this.membersManualToggled = false
            }
        },
        onlineUsers(newUsers = [], oldUsers = []) {
            if (!this.syncRoomId) return
            if (!this.membersSnapshotReady) {
                this.membersSnapshotReady = true
                if (!this.membersManualToggled) {
                    this.membersCollapsed = newUsers.length > MEMBER_COLLAPSE_THRESHOLD
                }
                return
            }

            if (!this.membersManualToggled) {
                this.membersCollapsed = newUsers.length > MEMBER_COLLAPSE_THRESHOLD
            }

            const oldMap = new Map(oldUsers.map((u) => [String(u.user_id), u]))
            const newMap = new Map(newUsers.map((u) => [String(u.user_id), u]))
            const ownerId = String(this.syncOwnerUserId || '')

            newUsers.forEach((u) => {
                const userId = String(u.user_id)
                if (!oldMap.has(userId)) {
                    if (userId === ownerId) {
                        this.pushSystemMessage(`${u.username} ${this.$tt('已重连房间')}`)
                    } else {
                        this.pushSystemMessage(`${u.username} ${this.$tt('加入房间')}`)
                    }
                }
            })

            oldUsers.forEach((u) => {
                const userId = String(u.user_id)
                if (!newMap.has(userId)) {
                    if (userId === ownerId) {
                        this.pushSystemMessage(`${u.username} ${this.$tt('连接中断，等待重连')}`)
                    } else {
                        this.pushSystemMessage(`${u.username} ${this.$tt('离开房间')}`)
                    }
                }
            })
        },
    },
    methods: {
        goBack() {
            if (window.history.length > 1) {
                this.$router.back()
                return
            }
            this.$router.push({ name: 'playing-local' })
        },
        goNowPlaying() {
            if (!this.currentTrack) {
                this.$router.push({ name: 'playing-local' })
                return
            }
            this.$router.push({ name: 'playingList', params: { id: this.currentTrack.id } })
        },
        toggleMembersCollapse() {
            this.membersCollapsed = !this.membersCollapsed
            this.membersManualToggled = true
        },
        isSameId(left, right) {
            if (left === null || left === undefined || right === null || right === undefined) {
                return false
            }
            return String(left) === String(right)
        },
        isMine(msg) {
            return this.isSameId(msg.user_id, this.syncCurrentUserId)
        },
        scrollChatToBottom() {
            this.$nextTick(() => {
                const chatBox = this.$refs.chatScroll
                if (chatBox) chatBox.scrollTop = chatBox.scrollHeight
            })
        },
        pushSystemMessage(text, createdAtMs = Date.now(), customId = '') {
            this.chatMessages.push({
                id: customId || `sys_${createdAtMs}_${Math.random()}`,
                type: 'system',
                message: text,
                created_at_ms: createdAtMs,
            })
            this.scrollChatToBottom()
        },
        getUserInitial(username) {
            const text = (username || '').trim()
            return text ? text.slice(0, 1).toUpperCase() : '?'
        },
        avatarGradient(username) {
            const text = (username || '').trim()
            let hash = 0
            for (let i = 0; i < text.length; i += 1) {
                hash = text.charCodeAt(i) + ((hash << 5) - hash)
            }
            const colors = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
            return `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`
        },
        formatChatTime(ms) {
            if (!ms) return ''
            const date = new Date(ms)
            const h = `${date.getHours()}`.padStart(2, '0')
            const m = `${date.getMinutes()}`.padStart(2, '0')
            return `${h}:${m}`
        },
        async loadCurrentLyrics() {
            if (!this.trackId || this.trackId === -1) return
            try {
                const res = await this.$api.subsonic.getLyrics(this.trackId)
                this.lyricsObjArr = this.formatLyrics(res.value)
            } catch (e) {
                this.lyricsObjArr = []
            }
        },
        async createSyncRoom() {
            if (this.syncBusy) return
            this.syncBusy = true
            try {
                const customRoomId = (this.syncRoomInput || '').trim()
                const data = await this.$store.dispatch('syncRoom/createRoom', {
                    name: '',
                    room_id: customRoomId || undefined,
                })
                this.syncRoomInput = data.room.room_id
                this.$myMsg.notify({ title: this.$tt('创建成功'), content: `${this.$tt('房间号')}: ${data.room.room_id}`, type: 'success', time: 1500 })
            } catch (error) {
                this.$myMsg.notify({ title: this.$tt('创建失败'), content: error.message || this.$tt('创建房间失败'), type: 'error', time: 2000 })
            } finally {
                this.syncBusy = false
            }
        },
        async joinSyncRoom() {
            if (!this.syncRoomInput || this.syncBusy) return
            this.syncBusy = true
            try {
                await this.$store.dispatch('syncRoom/joinRoom', this.syncRoomInput)
                this.$myMsg.notify({ title: this.$tt('加入成功'), content: `${this.$tt('已加入房间')} ${this.syncRoomInput}`, type: 'success', time: 1500 })
            } catch (error) {
                this.$myMsg.notify({ title: this.$tt('加入失败'), content: error.message || this.$tt('加入房间失败'), type: 'error', time: 2000 })
            } finally {
                this.syncBusy = false
            }
        },
        async leaveSyncRoom() {
            if (!this.syncRoomId || this.syncBusy) return
            this.syncBusy = true
            try {
                await this.$store.dispatch('syncRoom/leaveRoom')
                this.$myMsg.notify({ title: this.$tt('已离开房间'), type: 'success', time: 1200 })
            } catch (error) {
                this.$myMsg.notify({ title: this.$tt('离开失败'), content: error.message || this.$tt('离开房间失败'), type: 'error', time: 2000 })
            } finally {
                this.syncBusy = false
            }
        },
        async sendChat() {
            if (!this.syncRoomId || !this.chatInput) return
            try {
                await this.$store.dispatch('syncRoom/sendChat', this.chatInput)
                this.chatInput = ''
            } catch (error) {
                this.$myMsg.notify({ title: this.$tt('发送失败'), content: error.message || this.$tt('消息发送失败'), type: 'error', time: 1500 })
            }
        },
        scrollToLyric(i) {
            if (this.syncReadonly) return
            const line = this.lyricsObjArr[i]
            if (!line) return
            const seek = line.time / (this.duration || 1)
            this.$store.dispatch('seek', seek)
        },
        scrollActiveLyricIntoView(index) {
            this.$nextTick(() => {
                const container = this.$refs.lyricContainer
                if (!container) return
                const lyricRefs = this.$refs.lyric
                const target = Array.isArray(lyricRefs) ? lyricRefs[index] : lyricRefs
                if (!target) return

                const targetCenter = target.offsetTop + (target.offsetHeight / 2)
                const desiredTop = targetCenter - (container.clientHeight / 2)
                const maxTop = Math.max(0, container.scrollHeight - container.clientHeight)
                container.scrollTo({
                    top: Math.max(0, Math.min(desiredTop, maxTop)),
                    behavior: 'smooth',
                })
            })
        },
        formatLyrics(lyrics) {
            if (!lyrics) return []
            const regNewLine = /\n/
            const lineArr = lyrics.split(regNewLine)
            const lyricsObjArr = []
            lineArr.forEach(item => {
                if (item === '') return
                const spiltList = item.split(']')
                const spiltListLength = spiltList.length
                const content = spiltList[spiltListLength - 1].trim()
                const timeArray = spiltList.slice(0, spiltListLength - 1)
                timeArray.forEach(dt => {
                    const obj = {}
                    obj.lyric = content === '' ? '' : content
                    obj.time = this.formatLyricTime(dt.slice(1))
                    obj.uid = Math.random().toString().slice(-6)
                    if (obj.lyric !== '') {
                        lyricsObjArr.push(obj)
                    }
                })
            })
            return lyricsObjArr.sort((a, b) => a.time - b.time)
        },
        formatLyricTime(item) {
            if (item.indexOf('.') === -1) {
                const timeParts = item.split(':')
                const hours = parseInt(timeParts[0], 10)
                const minutes = parseInt(timeParts[1], 10)
                const seconds = parseInt(timeParts[2], 10)
                const totalTime = hours * 60 * 60 + minutes * 60 + seconds
                return Number(totalTime)
            }
            const regTime = /\d{2}:\d{2}.\d{2,3}/
            const isTime = item.match(regTime)
            const time = isTime ? isTime[0] : '00:00.00'
            const regMin = /.*:/
            const regSec = /:.*\./
            const regMs = /\./
            const min = parseInt(time.match(regMin)[0].slice(0, 2), 10)
            let sec = parseInt(time.match(regSec)[0].slice(1, 3), 10)
            const ms = time.slice(time.match(regMs).index + 1, time.match(regMs).index + 3)
            if (min !== 0) {
                sec += min * 60
            }
            return Number(`${sec}.${ms}`)
        },
    }
}
</script>

<style scoped>
.listener-groupcast {
    height: calc(100dvh - 12.25rem);
    padding: 0.45rem 0.1rem 0.25rem;
    box-sizing: border-box;
    overflow-y: auto;
}

.listener-groupcast__hero {
    padding: 0.3rem 0.25rem 0.2rem;
}

.listener-groupcast__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.listener-groupcast__heading {
    flex: 1;
    min-width: 0;
    text-align: center;
}

.listener-groupcast__eyebrow {
    font-size: 0.8rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-groupcast__caption {
    margin-top: 0.12rem;
    font-size: 0.84rem;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-groupcast__icon-button {
    width: 2.8rem;
    height: 2.8rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.84);
    color: #111827;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.16);
    flex-shrink: 0;
}

.listener-groupcast__icon-button svg {
    width: 1.2rem;
    height: 1.2rem;
}

.listener-groupcast__summary {
    margin-top: 1rem;
    padding: 1rem;
    border-radius: 1.65rem;
    background: linear-gradient(135deg, rgba(17, 24, 39, 0.96), rgba(55, 65, 81, 0.92));
    color: #fff;
    box-shadow: 0 20px 46px rgba(15, 23, 42, 0.16);
}

.listener-groupcast__summary-kicker {
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.64);
}

.listener-groupcast__summary-title {
    margin-top: 0.35rem;
    font-size: 1.08rem;
    font-weight: 700;
    line-height: 1.4;
}

.listener-groupcast__summary-sub {
    margin-top: 0.32rem;
    font-size: 0.8rem;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.68);
}

.listener-groupcast__summary-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.65rem;
    margin-top: 0.9rem;
}

.listener-groupcast__summary-action {
    border: 0;
    border-radius: 1rem;
    padding: 0.78rem 0.65rem;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 0.82rem;
    font-weight: 700;
}

.listener-groupcast__summary-action--primary {
    background: #fff;
    color: #111827;
}

.listener-groupcast__join-card,
.listener-groupcast__members-compact,
.listener-groupcast__member,
.listener-groupcast__queue-wrap,
.listener-groupcast__lyrics,
.listener-groupcast__chat-list {
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.12);
}

.listener-groupcast__join-card {
    margin-top: 0.95rem;
    padding: 1rem;
    border-radius: 1.5rem;
}

.listener-groupcast__join-kicker {
    font-size: 0.74rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-groupcast__join-row,
.listener-groupcast__chat-input-row {
    display: flex;
    gap: 0.7rem;
    margin-top: 0.7rem;
}

.listener-groupcast__join-input,
.listener-groupcast__chat-input {
    min-width: 0;
    flex: 1;
    border: 0;
    border-radius: 1rem;
    padding: 0.9rem 1rem;
    background: rgba(241, 245, 249, 0.88);
    color: #111827;
    font-size: 0.86rem;
}

.listener-groupcast__join-button,
.listener-groupcast__chat-send {
    border: 0;
    border-radius: 1rem;
    padding: 0 1rem;
    min-width: 4.9rem;
    background: #111827;
    color: #fff;
    font-size: 0.82rem;
    font-weight: 700;
}

.listener-groupcast__join-tip,
.listener-groupcast__owner-tip {
    margin-top: 0.65rem;
    font-size: 0.76rem;
    line-height: 1.6;
    color: #64748b;
}

.listener-groupcast__owner-tip {
    color: #b45309;
}

.listener-groupcast__section {
    margin-top: 1.3rem;
    padding: 0 0.25rem;
}

.listener-groupcast__section--chat {
    padding-bottom: 0.6rem;
}

.listener-groupcast__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.9rem;
}

.listener-groupcast__section-header h2 {
    font-size: 1.3rem;
    line-height: 1.2;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.04em;
}

.listener-groupcast__section-header button {
    border: 0;
    background: transparent;
    color: #94a3b8;
    font-size: 0.82rem;
    font-weight: 700;
}

.listener-groupcast__members-compact {
    padding: 0.95rem 1rem;
    border-radius: 1.35rem;
    font-size: 0.82rem;
    color: #64748b;
    line-height: 1.6;
}

.listener-groupcast__members {
    display: grid;
    gap: 0.72rem;
}

.listener-groupcast__member {
    display: flex;
    align-items: center;
    gap: 0.82rem;
    padding: 0.78rem 0.9rem;
    border-radius: 1.35rem;
}

.listener-groupcast__member-avatar {
    width: 2.7rem;
    height: 2.7rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 0.9rem;
    font-weight: 700;
    flex-shrink: 0;
}

.listener-groupcast__member-meta {
    min-width: 0;
    flex: 1;
}

.listener-groupcast__member-name {
    font-size: 0.9rem;
    font-weight: 700;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-groupcast__member-role {
    margin-top: 0.18rem;
    font-size: 0.74rem;
    color: #94a3b8;
}

.listener-groupcast__member-tag {
    flex-shrink: 0;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    background: #111827;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
}

.listener-groupcast__queue-wrap,
.listener-groupcast__lyrics,
.listener-groupcast__chat-list {
    border-radius: 1.5rem;
    padding: 0.95rem;
}

.listener-groupcast__lyrics {
    max-height: 18rem;
    overflow-y: auto;
}

.listener-groupcast__lyric-line {
    width: 100%;
    border: 0;
    background: transparent;
    padding: 0.38rem 0;
    text-align: center;
    color: #94a3b8;
    font-size: 0.88rem;
    line-height: 1.7;
}

.listener-groupcast__lyric-line.is-active {
    color: #111827;
    font-weight: 700;
}

.listener-groupcast__chat-list {
    max-height: 18rem;
    overflow-y: auto;
}

.listener-groupcast__chat-system {
    margin: 0.55rem 0;
    text-align: center;
}

.listener-groupcast__chat-system span {
    display: inline-block;
    padding: 0.34rem 0.7rem;
    border-radius: 999px;
    background: rgba(241, 245, 249, 0.96);
    color: #64748b;
    font-size: 0.72rem;
}

.listener-groupcast__chat-bubble-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 0.75rem;
}

.listener-groupcast__chat-bubble-wrap.is-mine {
    align-items: flex-end;
}

.listener-groupcast__chat-meta {
    display: flex;
    gap: 0.42rem;
    margin-bottom: 0.28rem;
    font-size: 0.7rem;
    color: #94a3b8;
}

.listener-groupcast__chat-bubble {
    max-width: 82%;
    padding: 0.72rem 0.85rem;
    border-radius: 1rem;
    background: rgba(241, 245, 249, 0.96);
    color: #111827;
    font-size: 0.82rem;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
}

.listener-groupcast__chat-bubble-wrap.is-mine .listener-groupcast__chat-bubble {
    background: #111827;
    color: #fff;
}

.listener-groupcast__empty-inline {
    text-align: center;
    color: #94a3b8;
    font-size: 0.82rem;
    padding: 0.5rem 0;
}

@media (max-width: 380px) {
    .listener-groupcast__summary-actions {
        grid-template-columns: minmax(0, 1fr);
    }

    .listener-groupcast__join-row,
    .listener-groupcast__chat-input-row {
        flex-direction: column;
    }

    .listener-groupcast__join-button,
    .listener-groupcast__chat-send {
        min-height: 2.9rem;
        width: 100%;
    }
}
</style>
